import { Container, IconButton, Stack, Tooltip } from '@chronosphereio/chrono-ui';
import { useFeatureFlags } from '@chronosphereio/core';
import { Check as CheckIcon, Close as CloseIcon } from 'mdi-material-ui';
import { useEffect, useRef } from 'react';
import { Group } from '@/dashboards/components/Builder';
import { Filter, isValidFilter } from '../promql-builder-model';
import { FilterLabelSelector } from './FilterLabelSelector';
import { FilterOperatorSelector } from './FilterOperatorSelector';
import { FilterValueSelector } from './FilterValueSelector';

type FilterEditorProps = {
  filter: Filter;
  onFilterChange: (filter: Filter) => void;
  onApply: () => void;
  onCancel: () => void;
};

export function FilterEditor({ filter, onFilterChange, onApply, onCancel }: FilterEditorProps) {
  const { panwRebrandStubYear } = useFeatureFlags();
  const labelSelectorRef = useRef<HTMLButtonElement>(null);

  // Focus the label selector when the editor loads.
  useEffect(() => {
    if (labelSelectorRef.current) {
      labelSelectorRef.current.focus();
    }
  }, []);

  return (
    <Container sx={{ padding: 1.5, display: 'flex', flexDirection: 'row' }}>
      <Group.Root>
        <FilterLabelSelector buttonRef={labelSelectorRef} isFirst filter={filter} onFilterChange={onFilterChange} />
        <FilterOperatorSelector filter={filter} onFilterChange={onFilterChange} />
        <FilterValueSelector isLast filter={filter} onFilterChange={onFilterChange} />
      </Group.Root>
      <Stack direction="row" ml={1} spacing={1}>
        {panwRebrandStubYear && <CancelButton onCancel={onCancel} />}
        <ApplyButton isDisabled={!isValidFilter(filter)} onApply={onApply} />
        {!panwRebrandStubYear && <CancelButton onCancel={onCancel} />}
      </Stack>
    </Container>
  );
}

function ApplyButton({ isDisabled, onApply }: { isDisabled: boolean; onApply: () => void }) {
  if (isDisabled) {
    return (
      <Tooltip Content="Select a label and value">
        <Stack direction="row" alignItems="center">
          <IconButton size="small" sx={{ width: '32px' }} disabled>
            <CheckIcon />
          </IconButton>
        </Stack>
      </Tooltip>
    );
  }

  return (
    // '32px' is to make sure the hover background is not cut off.
    <IconButton size="small" sx={{ width: '32px' }} onClick={onApply}>
      <CheckIcon />
    </IconButton>
  );
}

function CancelButton({ onCancel }: { onCancel: () => void }) {
  return (
    // '32px' is to make sure the hover background is not cut off.
    <IconButton size="small" sx={{ width: '32px' }} onClick={onCancel}>
      <CloseIcon />
    </IconButton>
  );
}
